import { z } from "zod";

/**
 * The category field was removed 2026-07-07, but historical audit rows still
 * carry it — these labels exist only for `audit-resolve.ts`. Never delete.
 */
export const ANNOUNCEMENT_CATEGORY_LABELS: Record<string, string> = {
  general: "General",
  company: "Company",
  people: "People",
  product: "Product",
};

export const announcementRecurrenceValues = ["none", "weekly", "monthly"] as const;
export type AnnouncementRecurrence = (typeof announcementRecurrenceValues)[number];

export const ANNOUNCEMENT_RECURRENCE_LABELS: Record<AnnouncementRecurrence, string> = {
  none: "One-time",
  weekly: "Weekly",
  monthly: "Monthly",
};

export const announcementIconValues = [
  "megaphone",
  "calendar",
  "clock",
  "celebration",
  "alert",
  "info",
] as const;
export type AnnouncementIcon = (typeof announcementIconValues)[number];

export const ANNOUNCEMENT_ICON_LABELS: Record<AnnouncementIcon, string> = {
  megaphone: "Megaphone",
  calendar: "Calendar",
  clock: "Clock",
  celebration: "Celebration",
  alert: "Alert",
  info: "Info",
};

// http(s) only — a javascript: URL in an announcement button would run in
// every employee's browser.
const announcementLinkUrl = z
  .string()
  .trim()
  .max(500)
  .url("Enter a full URL, e.g. https://…")
  .refine((u) => /^https?:\/\//i.test(u), "Link must start with http:// or https://");

/** Both together or neither — a button needs a label AND a destination. */
function linkPairComplete(d: { linkUrl?: string | null; linkLabel?: string | null }): boolean {
  return (d.linkUrl == null) === (d.linkLabel == null);
}
/** Hex background like #dbeafe — the only shape the color picker produces. */
const announcementBgColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "Pick a color")
  .nullable();

const LINK_PAIR = {
  message: "Provide both a button label and a URL, or neither",
  path: ["linkLabel"],
} as const;

// Default-free base — updates derive from this so .partial() can't
// re-inject defaults into fields the caller didn't send. Announcements have
// no end time: they show until each user dismisses them.
const announcementBaseSchema = z.object({
  title: z.string().min(1, "Give it a title").max(200),
  body: z.string().min(1, "Say something").max(5000),
  icon: z.enum(announcementIconValues),
  linkUrl: announcementLinkUrl.nullable(),
  linkLabel: z.string().trim().min(1).max(60).nullable(),
  bgColor: announcementBgColor,
  startsAt: z.coerce.date(),
  recurrence: z.enum(announcementRecurrenceValues),
});

export const announcementCreateSchema = announcementBaseSchema
  .extend({
    // Default start is "now" — applied server-side when omitted.
    startsAt: z.coerce.date().optional(),
    recurrence: z.enum(announcementRecurrenceValues).default("none"),
    icon: z.enum(announcementIconValues).default("megaphone"),
    linkUrl: announcementLinkUrl.nullable().optional(),
    linkLabel: z.string().trim().min(1).max(60).nullable().optional(),
    bgColor: announcementBgColor.optional(),
  })
  .refine((d) => linkPairComplete({ linkUrl: d.linkUrl, linkLabel: d.linkLabel }), LINK_PAIR);

export const announcementUpdateSchema = announcementBaseSchema.partial().refine(
  // Only enforce the pairing when the request touches the link at all —
  // a title-only PATCH must not be rejected for fields it didn't send.
  (d) => (d.linkUrl === undefined && d.linkLabel === undefined) || linkPairComplete(d),
  LINK_PAIR,
);

export type AnnouncementCreateInput = z.infer<typeof announcementCreateSchema>;
export type AnnouncementUpdateInput = z.infer<typeof announcementUpdateSchema>;
